package com.loyola.acessoescola.ui.addnew

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.loyola.acessoescola.services.QuickAuthClient
import com.loyola.acessoescola.services.quintadb.AcessoEscolaAPI
import com.loyola.acessoescola.services.quintadb.ErrorMessages
import com.loyola.acessoescola.services.quintadb.ExpiredTokenException
import com.loyola.acessoescola.services.quintadb.FaceDetectionException
import com.loyola.acessoescola.ui.isValidCPF
import com.loyola.acessoescola.ui.loadImageBitMap
import com.loyola.acessoescola.ui.uuid
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AddNewViewModel(
    private val acessoEscolaAPI: AcessoEscolaAPI,
    private val quickAuthClient: QuickAuthClient
): ViewModel() {
    private val _uiState = MutableStateFlow(AddNewState())

    val uiState: StateFlow<AddNewState> = _uiState.asStateFlow()

    fun stateFromRecordID(recordID: String, onErrorCallback: (String) -> Unit){

        quickAuthClient.getCurrentAccount({}){ _, idToken ->
            viewModelScope.launch {
                try {
                    acessoEscolaAPI.getRecords(
                        userIDToken = idToken ?: "",
                    ).collect { jsonOutValues ->
                        val record = jsonOutValues.first { it.recordID == recordID }
                        val values = record.jsonValues
                        _uiState.update { currentState ->
                            currentState.copy(
                                recordID = record.recordID,
                                phone = values.phone,
                                cpf = values.cpf,
                                bound = values.bound,
                                fullName = values.name,
                                email = values.email,
                                photoUri = null,
                                photoBitmap = null
                            )
                        }
                    }
                } catch (e: Exception){
                    onErrorCallback(e.message ?: ErrorMessages.UNKNOWN.message)
                }
            }
        }
    }

    fun resetState() {
        _uiState.update { currentState ->
            currentState.copy(
                recordID = null,
                phone = "",
                cpf = "",
                bound = Bounds.NONE.string,
                fullName = "",
                email = "",
                photoBitmap = null,
                photoUri = null,
                saveRequested = false,
                fullNameError = false,
                cpfError = false,
                boundError = false,
                phoneError = false,
                photoError = false
            )
        }
    }

    fun onFullNameChange(value: String){
        _uiState.update { currentState ->
            currentState.copy(
                fullName = value
            )
        }
    }
    fun onCpfChange(value: String){
        if (value.length <= 11) {
            _uiState.update { currentState ->
                currentState.copy(
                    cpf = value
                )
            }
        }
    }
    fun onBoundChange(value: String){
        _uiState.update { currentState ->
            currentState.copy(
                bound = value
            )
        }
    }
    fun onPhoneChange(value: String){
        if (value.length <= 11) {
            _uiState.update { currentState ->
                currentState.copy(
                    phone = value
                )
            }
        }
    }
    suspend fun onPhotoUriChange(value: String){
        _uiState.update { currentState ->
            currentState.copy(
                photoUri = value,
                photoBitmap = loadImageBitMap(value)
            )
        }
    }

    private fun checkEmptyEntries(): Boolean{
        _uiState.update { currentState ->
            currentState.copy(
                fullNameError = _uiState.value.fullName == "",
                cpfError = _uiState.value.cpf == "",
                boundError = _uiState.value.bound == Bounds.NONE.string,
                phoneError = _uiState.value.phone == "",
                photoError = _uiState.value.photoUri == null && _uiState.value.recordID == null
            )
        }
        val resultList: List<Boolean> = listOf(
            _uiState.value.fullNameError,
            _uiState.value.cpfError,
            _uiState.value.boundError,
            _uiState.value.phoneError,
            _uiState.value.photoError
        )

        return resultList.any { it }
    }

    fun saveRecord(onErrorCallback: (String) -> Unit, onSuccessCallBack: () -> Unit = {}){
        try {
            if (checkEmptyEntries()) {
                throw EmptyFieldException(AddNewErrorMessages.EMPTY_FIELDS.message)
            }

            _uiState.update { currentState ->
                currentState.copy(
                    cpfError = !isValidCPF(_uiState.value.cpf),
                )
            }

            if (_uiState.value.cpfError){
                throw InvalidCPFException(AddNewErrorMessages.INVALID_CPF.message)
            }

            _uiState.update { currentState ->
                currentState.copy(
                    phoneError = _uiState.value.phone.length < 11
                )
            }

            if (_uiState.value.phoneError){
                throw InvalidPhoneException(AddNewErrorMessages.INVALID_PHONE.message)
            }
        } catch (e: Exception){
            onErrorCallback(e.message ?: AddNewErrorMessages.SAVE_FAIL.message)
            return
        }
        _uiState.update { it.copy(saveRequested = true) }

        quickAuthClient.getCurrentAccount({}) { email, idToken ->
            viewModelScope.launch {

                if (_uiState.value.recordID == null) {
                    _uiState.value.photoUri?.let {
                        _uiState.update { currentState ->
                            currentState.copy(
                                recordID = _uiState.value.recordID ?: uuid(),
                                email = email ?: ""
                            )
                        }
                        try {
                            acessoEscolaAPI.createRecord(
                                imageUri = _uiState.value.photoUri!!,
                                userIDToken = idToken ?: "",
                                record = _uiState.value.toJsonInValues(),
                                onSuccessCallback = onSuccessCallBack
                            )
                        } catch(e: ExpiredTokenException){
                            onErrorCallback(e.message ?: ErrorMessages.UNKNOWN.message)
                            quickAuthClient.singout({}, {})
                            quickAuthClient.sign({}, {})
                        } catch (e: FaceDetectionException) {
                            onErrorCallback(e.message ?: ErrorMessages.UNKNOWN.message)
                            _uiState.update { currentState ->
                                currentState.copy(
                                    recordID = null,
                                    saveRequested = false
                                )
                            }
                        } catch (e: Exception) {
                            onErrorCallback(e.message ?: ErrorMessages.UNKNOWN.message)
                            _uiState.update { currentState ->
                                currentState.copy(
                                    email = email ?: "",
                                )
                            }
                        }
                    }
                } else {
                    try {
                        acessoEscolaAPI.updateRecord(
                            imageUri = _uiState.value.photoUri,
                            userIDToken = idToken ?: "",
                            record = _uiState.value.toJsonInValues(),
                            onSuccessCallback = onSuccessCallBack
                        )
                    } catch(e: ExpiredTokenException){
                        onErrorCallback(e.message ?: ErrorMessages.UNKNOWN.message)
                        quickAuthClient.singout({}, {})
                        quickAuthClient.sign({}, {})
                    } catch (e: FaceDetectionException) {
                        onErrorCallback(e.message ?: ErrorMessages.UNKNOWN.message)
                        _uiState.update { currentState ->
                            currentState.copy(
                                saveRequested = false,
                                recordID = null
                            )
                        }
                    }  catch (e: Exception) {
                        onErrorCallback(e.message ?: ErrorMessages.UNKNOWN.message)
                        _uiState.update { currentState ->
                            currentState.copy(
                                email = email ?: "",
                                saveRequested = false
                            )
                        }
                    }
                }
            }
        }
    }
}